/* eslint-disable max-lines */
import { sumBy } from 'lodash';
import React from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    Divider,
    Stack,
    Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import { DataGrid, DataGridCard } from '@barboxx/admin-components';
import {
    currencyFormatter,
    dateFormatter,
    ID_WIDTH,
    useAuth,
} from '@barboxx/admin-utils';
import { Order } from '@barboxx/models';
import { ORDER_PAYMENT_ADJUSTMENT_STATUS } from '@barboxx/types';
import { USD } from '@barboxx/utils';

import { BaseTextInput } from './BaseTextInput';

function ReceiptLineItem({
    label,
    value,
    emphasize = false,
}: {
    label: string;
    value: number;
    emphasize?: boolean;
}) {
    return (
        <Stack direction="row" justifyContent="space-between" gap={2}>
            <Typography
                variant={emphasize ? 'subtitle1' : 'body2'}
                fontWeight={emphasize ? 700 : 400}
            >
                {label}
            </Typography>
            <Typography
                variant={emphasize ? 'subtitle1' : 'body2'}
                fontWeight={emphasize ? 700 : 400}
            >
                {USD(value).toString()}
            </Typography>
        </Stack>
    );
}

const ADJUSTMENT_COLUMNS: GridColDef<Order.PaymentAdjustmentSummary>[] = [
    { field: 'id', headerName: 'Id', width: ID_WIDTH },
    {
        field: 'adjustedAt',
        headerName: 'Adjusted At',
        width: 200,
        valueFormatter: dateFormatter,
    },
    {
        field: 'adjustmentType',
        headerName: 'Type',
        width: 180,
    },
    {
        field: 'status',
        headerName: 'Status',
        width: 120,
    },
    {
        field: 'amount',
        headerName: 'Amount',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: currencyFormatter,
    },
    {
        field: 'previousTotalAmount',
        headerName: 'Previous Total',
        width: 150,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: currencyFormatter,
    },
    {
        field: 'nextTotalAmount',
        headerName: 'Next Total',
        width: 150,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: currencyFormatter,
    },
    {
        field: 'paymentIntentId',
        headerName: 'Payment Intent',
        width: 220,
    },
    {
        field: 'stripeRefundId',
        headerName: 'Refund Id',
        width: 220,
    },
    {
        field: 'reason',
        headerName: 'Reason',
        width: 220,
    },
    {
        field: 'adjustedBy',
        headerName: 'Adjusted By',
        width: 220,
        sortable: false,
        valueGetter: (value, row) => {
            const adjustedByUserName = row.adjustedByUser
                ? `${row.adjustedByUser.firstName} ${row.adjustedByUser.lastName}`.trim()
                : '';

            if (adjustedByUserName) {
                return adjustedByUserName;
            }

            const adjustedByCustomerDisplayName =
                row.adjustedByCustomer?.displayName?.trim() ?? '';
            if (adjustedByCustomerDisplayName) {
                return adjustedByCustomerDisplayName;
            }

            const adjustedByCustomerName = row.adjustedByCustomer
                ? `${row.adjustedByCustomer.firstName} ${row.adjustedByCustomer.lastName}`.trim()
                : '';
            if (adjustedByCustomerName) {
                return adjustedByCustomerName;
            }

            if (row.adjustedByUserId) {
                return `User #${row.adjustedByUserId}`;
            }

            if (row.adjustedByCustomerId) {
                return `Customer #${row.adjustedByCustomerId}`;
            }

            return 'System';
        },
    },
];

interface Props {
    order: Order.DetailsResponse;
}

export function OrderTransactionsCard({ order }: Props) {
    const {
        auth: { user },
    } = useAuth();

    const adjustments = React.useMemo(
        () => order.paymentAdjustments ?? [],
        [order.paymentAdjustments]
    );

    const adjustmentsChronological = React.useMemo(() => {
        return [...adjustments].sort((left, right) => {
            const leftAdjustedAt = Date.parse(left.adjustedAt || '');
            const rightAdjustedAt = Date.parse(right.adjustedAt || '');

            if (
                Number.isFinite(leftAdjustedAt) &&
                Number.isFinite(rightAdjustedAt) &&
                leftAdjustedAt !== rightAdjustedAt
            ) {
                return leftAdjustedAt - rightAdjustedAt;
            }

            return Number(left.id || 0) - Number(right.id || 0);
        });
    }, [adjustments]);

    const refundedAmount = React.useMemo(
        () =>
            sumBy(adjustments, adjustment => {
                if (
                    adjustment.status !==
                    ORDER_PAYMENT_ADJUSTMENT_STATUS.Succeeded
                ) {
                    return 0;
                }

                return Number(adjustment.amount || 0);
            }),
        [adjustments]
    );

    const finalChargeAmount = React.useMemo(() => {
        const latestAdjustmentWithNextTotal = [...adjustmentsChronological]
            .reverse()
            .find(adjustment => adjustment.nextTotalAmount != null);

        if (latestAdjustmentWithNextTotal) {
            return Number(latestAdjustmentWithNextTotal.nextTotalAmount);
        }

        return Number(order.totalAmount || 0);
    }, [adjustmentsChronological, order.totalAmount]);

    const originalChargeAmount = React.useMemo(() => {
        const earliestAdjustmentWithPreviousTotal =
            adjustmentsChronological.find(
                adjustment => adjustment.previousTotalAmount != null
            );

        if (earliestAdjustmentWithPreviousTotal) {
            return Number(
                earliestAdjustmentWithPreviousTotal.previousTotalAmount
            );
        }

        return finalChargeAmount + refundedAmount;
    }, [adjustmentsChronological, finalChargeAmount, refundedAmount]);

    const successfulRefundCount = React.useMemo(
        () =>
            adjustments.filter(
                adjustment =>
                    adjustment.status ===
                    ORDER_PAYMENT_ADJUSTMENT_STATUS.Succeeded
            ).length,
        [adjustments]
    );

    const cardDetails = React.useMemo(() => {
        if (order.cardBrand && order.cardLast4) {
            return `${order.cardBrand.toUpperCase()} •••• ${order.cardLast4}`;
        }

        if (order.cardLast4) {
            return `•••• ${order.cardLast4}`;
        }

        return 'N/A';
    }, [order.cardBrand, order.cardLast4]);

    const latestAdjustmentAt = adjustments[0]?.adjustedAt ?? null;

    return (
        <Stack gap={2}>
            <Card>
                <CardHeader
                    title="Transactions"
                    subheader="Receipt breakdown, settlement, and payment metadata"
                />
                <CardContent>
                    <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                        <Stack
                            gap={1.5}
                            flex={1}
                            sx={{
                                border: theme =>
                                    `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                                padding: 2,
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight={700}>
                                Receipt
                            </Typography>
                            <ReceiptLineItem
                                label="Sub Total"
                                value={order.subTotal}
                            />
                            <ReceiptLineItem
                                label="Service Fee"
                                value={order.serviceFee}
                            />
                            <ReceiptLineItem
                                label="Tax"
                                value={order.taxAmount}
                            />
                            <ReceiptLineItem
                                label="Tip"
                                value={order.tipAmount}
                            />
                            {order.creditCardProcessingFee > 0 && (
                                <ReceiptLineItem
                                    label="Credit Card Processing Fee"
                                    value={order.creditCardProcessingFee}
                                />
                            )}
                            <Divider sx={{ marginY: 0.5 }} />
                            <ReceiptLineItem
                                label="Original Charge"
                                value={originalChargeAmount}
                            />
                            {refundedAmount > 0 && (
                                <ReceiptLineItem
                                    label="Refunds"
                                    value={-refundedAmount}
                                />
                            )}
                            <ReceiptLineItem
                                label="Final Charge"
                                value={finalChargeAmount}
                                emphasize
                            />
                        </Stack>
                        <Stack gap={2} flex={1}>
                            <BaseTextInput
                                label="Payment Adjustments"
                                value={adjustments.length}
                            />
                            <BaseTextInput
                                label="Successful Refunds"
                                value={successfulRefundCount}
                            />
                            <BaseTextInput
                                label="Net Charged"
                                value={USD(finalChargeAmount).toString()}
                            />
                            <BaseTextInput
                                label="Total Refunded"
                                value={USD(refundedAmount).toString()}
                            />
                            <BaseTextInput
                                label="Expected Payout"
                                value={USD(
                                    order.totalAmount - order.applicationFee
                                ).toString()}
                            />
                            {user.userType === 'Internal' && (
                                <BaseTextInput
                                    label="Application Fee"
                                    value={USD(order.applicationFee).toString()}
                                />
                            )}
                        </Stack>
                        <Stack gap={2} flex={1}>
                            <BaseTextInput
                                label="Payment Intent"
                                value={order.paymentIntent ?? 'N/A'}
                            />
                            <BaseTextInput label="Card" value={cardDetails} />
                            <BaseTextInput
                                label="Paid At"
                                value={order.paidAt ?? 'N/A'}
                            />
                            <BaseTextInput
                                label="Latest Adjustment At"
                                value={latestAdjustmentAt ?? 'N/A'}
                            />
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            <DataGridCard title="Payment Adjustments">
                {adjustments.length > 0 ? (
                    <DataGrid
                        columns={ADJUSTMENT_COLUMNS}
                        rows={adjustments}
                        disableColumnMenu
                        disableRowSelectionOnClick
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No payment adjustments were recorded for this order.
                    </Typography>
                )}
            </DataGridCard>
        </Stack>
    );
}
/* eslint-enable max-lines */
